import { useEffect, useRef } from "react";
import type { RefObject } from "react";

interface UseTipsTransactionsInfiniteScrollParams {
  readonly hasMoreTransactions: boolean;
  readonly itemCount: number;
  readonly onLoadMore: () => Promise<void>;
}

export const useTipsTransactionsInfiniteScroll = (
  params: UseTipsTransactionsInfiniteScrollParams,
): RefObject<HTMLDivElement> => {
  const { hasMoreTransactions, itemCount, onLoadMore } = params;
  const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const triggerElement = loadMoreTriggerRef.current;
    if (!triggerElement || !hasMoreTransactions) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void onLoadMore();
        }
      },
      { rootMargin: "240px" },
    );

    observer.observe(triggerElement);
    return () => {
      observer.disconnect();
    };
  }, [hasMoreTransactions, itemCount, onLoadMore]);

  useEffect(() => {
    if (!hasMoreTransactions) return;

    const handleScroll = (): void => {
      const documentElement = document.documentElement;
      const distanceToBottom =
        documentElement.scrollHeight - window.scrollY - window.innerHeight;

      if (distanceToBottom < 480) {
        void onLoadMore();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [hasMoreTransactions, onLoadMore]);

  return loadMoreTriggerRef;
};
